import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { api } from '../api';
import DatasheetPreview from './DatasheetPreview';
import USDatasheetPreview from './USDatasheetPreview';
import { isUsPackSheet } from './PackSheet';
import { useRegion } from '../region';
import CoverPage from './CoverPage';
import LibraryPicker from './LibraryPicker';
import { FileUpload } from './ImageUpload';
import { sheetHasPage2 } from '../datasheet';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// Shared cache of archive file bytes so page-split packs don't re-download the
// same PDF once per page. pdf.js transfers buffers to its worker, so callers
// must pass buf.slice(0).
const legacyBufCache = {};
async function getLegacyBuf(id) {
  if (!legacyBufCache[id]) {
    legacyBufCache[id] = (async () => {
      const url = await api.getLegacyObjectUrl(id);
      const buf = await (await fetch(url)).arrayBuffer();
      return { url, buf };
    })();
  }
  return legacyBufCache[id];
}

// ---------- highlight primitives ----------

export function HighlightRects({ rects }) {
  return (
    <>
      {(rects || []).map((r, i) => (
        <div key={i + ':' + r.x + ',' + r.y} className="hl-rect" style={{ left: r.x + '%', top: r.y + '%', width: r.w + '%', height: r.h + '%' }} />
      ))}
    </>
  );
}

// Snap a drag rectangle to the text lines beneath it, like a real highlighter.
// Falls back to the raw rectangle when nothing textual is under the drag (images).
function snapDragToText(container, layerEl, drag) {
  const L = layerEl.getBoundingClientRect();
  const dr = {
    left: L.left + (drag.x / 100) * L.width,
    top: L.top + (drag.y / 100) * L.height,
  };
  dr.right = dr.left + (drag.w / 100) * L.width;
  dr.bottom = dr.top + (drag.h / 100) * L.height;
  // Forgiving vertical band for thin horizontal swipes across a line of text.
  if (dr.bottom - dr.top < 6) { dr.top -= 4; dr.bottom += 4; }
  const segs = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    if (!node.textContent.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    Array.from(range.getClientRects()).forEach(r => {
      if (r.width < 2 || r.height < 2) return;
      if (r.left < dr.right && r.right > dr.left && r.top < dr.bottom && r.bottom > dr.top) {
        segs.push({
          left: Math.max(r.left, dr.left), right: Math.min(r.right, dr.right),
          top: r.top, bottom: r.bottom,
        });
      }
    });
  }
  if (!segs.length) return (drag.w > 0.4 && drag.h > 0.4) ? [drag] : [];
  // Merge overlapping/adjacent segments that sit on the same text line.
  segs.sort((a, b) => (a.top - b.top) || (a.left - b.left));
  const merged = [];
  segs.forEach(s => {
    const last = merged[merged.length - 1];
    if (last && Math.abs(last.top - s.top) < 3 && s.left <= last.right + 8) {
      last.right = Math.max(last.right, s.right);
      last.bottom = Math.max(last.bottom, s.bottom);
    } else merged.push({ ...s });
  });
  const pad = 1.5;
  return merged.map(m => ({
    x: +(((m.left - L.left) / L.width) * 100).toFixed(2),
    y: +(((m.top - pad - L.top) / L.height) * 100).toFixed(2),
    w: +(((m.right - m.left) / L.width) * 100).toFixed(2),
    h: +(((m.bottom - m.top + pad * 2) / L.height) * 100).toFixed(2),
  })).filter(r => r.w > 0.1 && r.h > 0.1);
}

function DrawLayer({ rects, active, onAdd, onRemove }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(null);
  const pct = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)),
    };
  };
  function down(e) {
    if (!active || e.button !== 0) return;
    e.preventDefault();
    const p = pct(e);
    setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  }
  function move(e) { if (draft) { const p = pct(e); setDraft(d => ({ ...d, x1: p.x, y1: p.y })); } }
  function up() {
    if (!draft) return;
    const rect = {
      x: +Math.min(draft.x0, draft.x1).toFixed(2), y: +Math.min(draft.y0, draft.y1).toFixed(2),
      w: +Math.abs(draft.x1 - draft.x0).toFixed(2), h: +Math.abs(draft.y1 - draft.y0).toFixed(2),
    };
    setDraft(null);
    if (rect.w > 0.3 || rect.h > 0.3) {
      const snapped = snapDragToText(ref.current.parentElement || ref.current, ref.current, rect);
      if (snapped.length) onAdd(snapped);
    }
  }
  const d = draft && {
    x: Math.min(draft.x0, draft.x1), y: Math.min(draft.y0, draft.y1),
    w: Math.abs(draft.x1 - draft.x0), h: Math.abs(draft.y1 - draft.y0),
  };
  return (
    <div ref={ref} className={'hl-layer' + (active ? ' active' : '')} data-testid="highlight-layer"
      onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}>
      {(rects || []).map((r, i) => (
        <div key={i} className="hl-rect" style={{ left: r.x + '%', top: r.y + '%', width: r.w + '%', height: r.h + '%' }}>
          {active && <button className="hl-x" data-testid={'hl-remove-' + i} title="Remove highlight"
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onRemove(i); }}>✕</button>}
        </div>
      ))}
      {d && <div className="hl-rect draft" style={{ left: d.x + '%', top: d.y + '%', width: d.w + '%', height: d.h + '%' }} />}
    </div>
  );
}

// ---------- movable TYPE boxes ----------

export function TypeBoxesStatic({ boxes }) {
  return (
    <>
      {(boxes || []).map(b => (
        <div key={b.id} className={'tb tb-static' + ((b.text || '').length > 9 ? ' long' : '')} style={{ left: b.x + '%', top: b.y + '%' }}>{b.text}</div>
      ))}
    </>
  );
}

function TypeBoxLayer({ boxes, onChange }) {
  const ref = useRef(null);
  function startDrag(e, b) {
    e.preventDefault(); e.stopPropagation();
    const L = ref.current.getBoundingClientRect();
    const el = e.currentTarget.parentElement;
    const r = el.getBoundingClientRect();
    const d = { dx: e.clientX - r.left, dy: e.clientY - r.top, x: b.x, y: b.y };
    const move = (ev) => {
      d.x = Math.min(86, Math.max(0, ((ev.clientX - d.dx - L.left) / L.width) * 100));
      d.y = Math.min(95, Math.max(0, ((ev.clientY - d.dy - L.top) / L.height) * 100));
      el.style.left = d.x + '%'; el.style.top = d.y + '%';
    };
    const up = () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      onChange((boxes || []).map(x => x.id === b.id ? { ...x, x: +d.x.toFixed(2), y: +d.y.toFixed(2) } : x));
    };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  }
  return (
    <div ref={ref} className="tb-layer">
      {(boxes || []).map(b => (
        <div key={b.id} className={'tb' + ((b.text || '').length > 9 ? ' long' : '')} style={{ left: b.x + '%', top: b.y + '%' }} data-testid={'type-box-' + b.id}>
          <span className="tb-drag" title="Drag to move" onMouseDown={e => startDrag(e, b)}>⠿</span>
          <input className="tb-input" defaultValue={b.text} data-testid={'type-box-input-' + b.id}
            onBlur={e => { if (e.target.value !== b.text) onChange((boxes || []).map(x => x.id === b.id ? { ...x, text: e.target.value } : x)); }} />
          <button className="tb-x" title="Remove" data-testid={'type-box-remove-' + b.id}
            onClick={() => onChange((boxes || []).filter(x => x.id !== b.id))}>✕</button>
        </div>
      ))}
    </div>
  );
}

// ---------- movable free-text boxes (Arial, adjustable size) ----------

export function TextBoxesStatic({ boxes }) {
  return (
    <>
      {(boxes || []).map(b => (
        <div key={b.id} className={'txb txb-static' + (b.hl ? ' hl' : '')} style={{ left: b.x + '%', top: b.y + '%', fontSize: (b.size || 14) + 'px' }}>{b.text}</div>
      ))}
    </>
  );
}

function TextBoxLayer({ boxes, onChange }) {
  const ref = useRef(null);
  function startDrag(e, b) {
    e.preventDefault(); e.stopPropagation();
    const L = ref.current.getBoundingClientRect();
    const el = e.currentTarget.closest('.txb');
    const r = el.getBoundingClientRect();
    const d = { dx: e.clientX - r.left, dy: e.clientY - r.top, x: b.x, y: b.y };
    const move = (ev) => {
      d.x = Math.min(92, Math.max(0, ((ev.clientX - d.dx - L.left) / L.width) * 100));
      d.y = Math.min(96, Math.max(0, ((ev.clientY - d.dy - L.top) / L.height) * 100));
      el.style.left = d.x + '%'; el.style.top = d.y + '%';
    };
    const up = () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      onChange((boxes || []).map(x => x.id === b.id ? { ...x, x: +d.x.toFixed(2), y: +d.y.toFixed(2) } : x));
    };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  }
  const patch = (b, p) => onChange((boxes || []).map(x => x.id === b.id ? { ...x, ...p } : x));
  return (
    <div ref={ref} className="tb-layer">
      {(boxes || []).map(b => (
        <div key={b.id} className={'txb' + (b.hl ? ' hl' : '')} style={{ left: b.x + '%', top: b.y + '%', fontSize: (b.size || 14) + 'px' }} data-testid={'text-box-' + b.id}>
          <div className="txb-bar">
            <span className="tb-drag" title="Drag to move" onMouseDown={e => startDrag(e, b)}>⠿</span>
            <button title="Smaller text" data-testid={'text-box-smaller-' + b.id} onClick={() => patch(b, { size: Math.max(8, (b.size || 14) - 2) })}>A−</button>
            <span className="txb-size">{b.size || 14}</span>
            <button title="Bigger text" data-testid={'text-box-bigger-' + b.id} onClick={() => patch(b, { size: Math.min(48, (b.size || 14) + 2) })}>A＋</button>
            <button className={'txb-hl' + (b.hl ? ' on' : '')} title={b.hl ? 'Remove highlight' : 'Highlight this text'} data-testid={'text-box-highlight-' + b.id} onClick={() => patch(b, { hl: !b.hl })}>HL</button>
            <button className="txb-del" title="Remove" data-testid={'text-box-remove-' + b.id} onClick={() => onChange((boxes || []).filter(x => x.id !== b.id))}>✕</button>
          </div>
          <textarea className="txb-input" defaultValue={b.text} rows={Math.max(1, (b.text || '').split('\n').length)}
            data-testid={'text-box-input-' + b.id}
            onBlur={e => { if (e.target.value !== b.text) patch(b, { text: e.target.value }); }} />
        </div>
      ))}
    </div>
  );
}

// ---------- AI auto-highlight measurement (uses the hidden export-host render) ----------

const norm = (t) => (t || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const SPEC_LABELS = [
  ['cri', ['CRI'], null],
  ['cct', ['COLOUR TEMP', 'COLOR TEMP', 'CCT'], null],
  ['wattage', ['WATTAGE', 'POWER'], null],
  ['dimming', ['DIMMING', 'DIM'], null],
  ['colour', ['COLOUR', 'COLOR', 'FINISH'], 'TEMP'],
];
function labelKey(txt) {
  const t = (txt || '').toUpperCase();
  for (const [k, words, excl] of SPEC_LABELS) {
    if (excl && t.includes(excl)) continue;
    if (words.some(w => t.includes(w))) return k;
  }
  return null;
}

export function computeAutoHighlights(itemId, specs) {
  const page = document.getElementById('pp_' + itemId);
  if (!page) return [];
  const p = page.getBoundingClientRect();
  if (!p.width) return [];
  const s = specs || {};
  const pct = (el) => {
    const r = el.getBoundingClientRect();
    return {
      x: +(((r.left - p.left) / p.width) * 100).toFixed(2), y: +(((r.top - p.top) / p.height) * 100).toFixed(2),
      w: +((r.width / p.width) * 100).toFixed(2), h: +((r.height / p.height) * 100).toFixed(2),
    };
  };
  const union = (a, b) => {
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    return { x, y, w: +(Math.max(a.x + a.w, b.x + b.w) - x).toFixed(2), h: +(Math.max(a.y + a.h, b.y + b.h) - y).toFixed(2) };
  };
  const rects = [];

  // Product details rows (dt/dd pairs) — only when the quote value actually appears
  page.querySelectorAll('.ds-details dt').forEach(dt => {
    const k = labelKey(dt.textContent);
    const val = k && (s[k] || '').trim();
    if (!val) return;
    const dd = dt.nextElementSibling;
    if (!dd) return;
    const nv = norm(val), nd = norm(dd.textContent);
    if (!nd || !(nd.includes(nv) || (nd.length >= 4 && nv.includes(nd)))) return;
    rects.push(union(pct(dt), pct(dd)));
  });

  // Common spec tables — precise: only highlight when the quote value is found
  page.querySelectorAll('.ds-common-table').forEach(tbl => {
    const ths = Array.from(tbl.querySelectorAll('thead th'));
    const tds = Array.from(tbl.querySelectorAll('tbody td'));
    ths.forEach((th, i) => {
      const k = labelKey(th.textContent);
      const val = k && (s[k] || '').trim();
      if (!val || !tds[i] || !(tds[i].textContent || '').trim()) return;
      const td = tds[i];
      const lines = (td.innerText || '').split('\n').map(x => x.trim()).filter(Boolean);
      const r = pct(td);
      const nv = norm(val);
      const idx = lines.findIndex(l => { const nl = norm(l); return nl.includes(nv) || (nl.length >= 4 && nv.includes(nl)); });
      if (idx < 0) return;
      if (lines.length > 1) {
        const lh = r.h / lines.length;
        rects.push({ x: r.x, y: +(r.y + idx * lh).toFixed(2), w: r.w, h: +lh.toFixed(2) });
      } else {
        rects.push(r);
      }
    });
  });

  // Variant table — matched model row + its wattage cell
  const model = norm(s.model || '');
  if (model && model.length >= 6) {
    const table = page.querySelector('.ds-table');
    if (table) {
      const heads = Array.from(table.querySelectorAll('thead tr.group-row th')).map(th => (th.textContent || '').toUpperCase());
      const wi = heads.findIndex(h => h.includes('WATT') || h.includes('POWER'));
      Array.from(table.querySelectorAll('tbody tr')).forEach(tr => {
        const cells = Array.from(tr.children);
        const hit = cells.some(td => {
          const t = norm(td.textContent);
          return t && t.length >= 6 && (model.includes(t) || t.includes(model));
        });
        if (hit) {
          cells.forEach(td => {
            const t = norm(td.textContent);
            if (t && t.length >= 6 && (model.includes(t) || t.includes(model))) rects.push(pct(td));
          });
          if (wi >= 0 && cells[wi] && (s.wattage || '').trim()) rects.push(pct(cells[wi]));
        }
      });
    }
    const codeEl = page.querySelector('.ds-product-code');
    if (codeEl) {
      const ct = norm(codeEl.textContent);
      if (ct && (model.includes(ct) || ct.includes(model))) rects.push(pct(codeEl));
    }
  }

  const seen = {};
  return rects.filter(r => { const k = [r.x, r.y, r.w, r.h].join(','); if (seen[k]) return false; seen[k] = 1; return true; });
}

// ---------- archive PDF page renderer ----------

function ArchivePages({ item, tool, onAddRect, onRemoveRect, onTypeBoxes, onTextBoxes }) {
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { url, buf } = await getLegacyBuf(item.legacyId);
        const ct = (item.contentType || '').toLowerCase();
        if (!ct.includes('pdf') && !/\.pdf$/i.test(item.name || '')) {
          if (!dead) setPages([{ img: url, w: 794, h: 1123 }]);
          return;
        }
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        const out = [];
        const from = item.page || 1;
        const to = item.page || doc.numPages;
        for (let n = from; n <= to; n++) {
          const page = await doc.getPage(n);
          const vp0 = page.getViewport({ scale: 1 });
          const scale = (794 * 1.6) / vp0.width;
          const vp = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width; canvas.height = vp.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
          out.push({ img: canvas.toDataURL('image/jpeg', 0.85), w: 794, h: Math.round(794 * (vp.height / vp.width)) });
        }
        if (!dead) setPages(out);
      } catch (e) { console.error('Archive render failed', e); if (!dead) setErr('Could not render "' + item.name + '"'); }
    })();
    return () => { dead = true; };
  }, [item.legacyId, item.contentType, item.name, item.page]);

  if (err) return <div className="empty-note" style={{ color: '#fff' }}>{err}</div>;
  if (!pages) return <div className="empty-note" style={{ color: '#fff' }}>Rendering PDF…</div>;
  const typeText = (item.typeOverlay || '').trim();
  return (
    <>
      {pages.map((pg, i) => {
        const key = item.page ? '1' : String(i + 1);
        return (
          <div key={'pg' + i} className="studio-page-wrap" style={{ width: pg.w, height: pg.h }}>
            <img src={pg.img} alt="" style={{ width: '100%', height: '100%', display: 'block', background: '#fff' }} />
            {i === 0 && typeText && <div className={'studio-type-preview' + (typeText.length > 12 ? ' long' : '')}>{typeText}</div>}
            <DrawLayer rects={(item.highlights || {})[key]} active={tool === 'hl'}
              onAdd={r => onAddRect(key, r)} onRemove={ri => onRemoveRect(key, ri)} />
            <TypeBoxLayer boxes={(item.typeBoxes || {})[key]} onChange={bs => onTypeBoxes(key, bs)} />
            <TextBoxLayer boxes={(item.textBoxes || {})[key]} onChange={bs => onTextBoxes(key, bs)} />
          </div>
        );
      })}
    </>
  );
}

// ---------- AI match modal ----------

function itemSummary(it) {
  if (it.type === 'archive') return { id: it.id, kind: 'uploaded_pdf_datasheet', name: it.name };
  const d = it.data || {};
  const cs = d.commonSpecs || {};
  return {
    id: it.id, kind: 'datasheet', name: d.name || it.name, variant: d.variant || '', code: d.code || it.code || '',
    models: (d.variants || []).map(v => v.model).filter(Boolean).slice(0, 25),
    wattages: (d.variants || []).map(v => v.systemPower).filter(Boolean).slice(0, 25),
    cri: cs.cri || '', cct: cs.cct || '', dimming: cs.dimming || '', colour: cs.colour || '',
  };
}

function AiMatchModal({ items, form, cover, setField, setCover, onApply, onClose, toast }) {
  const [file, setFile] = useState(null);
  const [salesRep, setSalesRep] = useState(cover.preparedBy || '');
  const [jobName, setJobName] = useState(form.name || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const nameById = {};
  items.forEach(it => { nameById[it.id] = (it.data && it.data.name) || it.name || it.id; });

  async function run() {
    if (!file) { toast('Choose the quote / sales order PDF first', true); return; }
    setBusy(true);
    try {
      // Include a text excerpt from each uploaded PDF so the AI can match on specs, not just filename.
      const summaries = [];
      for (const it of items) {
        if (it.type !== 'archive') { summaries.push(itemSummary(it)); continue; }
        let text = '';
        const ct = (it.contentType || '').toLowerCase();
        if (ct.includes('pdf') || /\.pdf$/i.test(it.name || '')) {
          try {
            const { buf } = await getLegacyBuf(it.legacyId);
            const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
            const from = it.page || 1;
            const to = it.page || Math.min(doc.numPages, 2);
            for (let n = from; n <= to && text.length < 1400; n++) {
              const tc = await (await doc.getPage(n)).getTextContent();
              text += tc.items.map(x => x.str).join(' ') + ' ';
            }
          } catch (e) { console.warn('pdf text extract failed for', it.name, e); }
        }
        summaries.push({ id: it.id, kind: 'uploaded_pdf_datasheet', name: it.name, text_excerpt: text.slice(0, 1400).trim() });
      }
      const res = await api.aiMatchQuote(file, summaries);
      setResult(res);
    } catch (e) {
      toast('AI match failed: ' + ((e.response && e.response.data && e.response.data.detail) || e.message), true);
    }
    setBusy(false);
  }

  async function apply() {
    if (jobName.trim() && jobName.trim() !== form.name) setField('name', jobName.trim());
    if (salesRep.trim() && salesRep.trim() !== cover.preparedBy) setCover('preparedBy', salesRep.trim());
    setBusy(true);
    try {
      await onApply(result);
      onClose();
    } catch (e) {
      toast('Apply failed: ' + (e.message || e), true);
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" data-testid="ai-match-modal" onMouseDown={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onMouseDown={e => e.stopPropagation()}>
        <h3>AI Match — quote to proposal</h3>
        <p className="modal-hint">Upload the customer quote / sales order. The AI matches each datasheet in the pack to its quote reference, fills the TYPE box, and highlights the matching core features (model, wattage, CRI, CCT, dim type, colour). When the same datasheet is quoted in several variations, a duplicate copy is created per variation — each with its own ref and highlights.</p>
        <div className="grid2">
          <div className="field"><label className="field-label">Job / project name</label>
            <input className="input" data-testid="ai-match-jobname" value={jobName} onChange={e => setJobName(e.target.value)} /></div>
          <div className="field"><label className="field-label">Sales rep (prepared by)</label>
            <input className="input" data-testid="ai-match-salesrep" value={salesRep} onChange={e => setSalesRep(e.target.value)} /></div>
        </div>
        <div className="field" style={{ marginTop: 8 }}>
          <label className="field-label">Quote / sales order PDF</label>
          <input type="file" accept="application/pdf,.pdf" data-testid="ai-match-file"
            onChange={e => setFile(e.target.files && e.target.files[0])} />
        </div>
        {busy && <div className="ai-pulse" style={{ marginTop: 12 }}>Reading the quote and matching datasheets… (20–60s)</div>}
        {result && !busy && (
          <div className="ai-match-result" data-testid="ai-match-result">
            <b>{(result.matches || []).length} match(es) found</b>
            <ul style={{ margin: '6px 0 0 18px' }}>
              {(result.matches || []).map((m, i) => {
                const isCopy = (result.matches || []).findIndex(x => x.item_id === m.item_id) !== i;
                return (
                  <li key={m.item_id + '|' + (m.refs || []).join(',') + '|' + i}><b>{(m.refs || []).join(' / ') || '—'}</b> → {nameById[m.item_id] || m.item_id}
                    {isCopy ? <em className="c-dup"> · duplicate will be created</em> : null}
                    {m.quote_code ? <em> · {m.quote_code}</em> : null} <span className={'conf conf-' + (m.confidence || 'low')}>{m.confidence}</span></li>
                );
              })}
            </ul>
            {(result.unmatched_quote_lines || []).length > 0 && (
              <div className="field-hint" style={{ marginTop: 8 }}>
                No datasheet in the pack for: {(result.unmatched_quote_lines || []).map(u => u.ref || u.product_code).filter(Boolean).join(', ')}
              </div>
            )}
            {(result.warnings || []).length > 0 && (
              <div className="field-hint" style={{ marginTop: 4 }}>{(result.warnings || []).join(' · ')}</div>
            )}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          {!result
            ? <button className="btn btn-primary" data-testid="ai-match-run" disabled={busy || !file} onClick={run}>{busy ? 'Matching…' : 'Run AI Match'}</button>
            : <button className="btn btn-primary" data-testid="ai-match-apply" disabled={busy} onClick={apply}>{busy ? 'Applying highlights…' : 'Apply ' + (result.matches || []).length + ' match(es)'}</button>}
        </div>
      </div>
    </div>
  );
}

// ---------- the studio ----------

export default function ProposalStudio({ items, form, setField, setCover, mutateItems, onEditItem, onClose, exportPdf, busy, toast }) {
  const region = useRegion();
  const [selIdx, setSelIdx] = useState(items.length ? 1 : 0); // 0 = cover
  const [tool, setTool] = useState('none');
  const [zoom, setZoom] = useState(0.72);
  const [aiOpen, setAiOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const cover = form.cover_sheet || {};
  const entries = [{ kind: 'cover' }, ...items.map(it => ({ kind: it.type === 'archive' ? 'archive' : 'sheet', it }))];
  const cur = entries[Math.min(selIdx, entries.length - 1)] || entries[0];

  const patchItem = useCallback((id, fn) => {
    mutateItems(items.map(x => (x.id === id ? fn(x) : x)));
  }, [items, mutateItems]);

  const addRects = (it, page, newRects) => patchItem(it.id, x => ({
    ...x, highlights: { ...(x.highlights || {}), [page]: [...(((x.highlights || {})[page]) || []), ...newRects] },
  }));
  const removeRect = (it, page, i) => patchItem(it.id, x => ({
    ...x, highlights: { ...(x.highlights || {}), [page]: (((x.highlights || {})[page]) || []).filter((_, j) => j !== i) },
  }));
  const clearRects = (it) => patchItem(it.id, x => ({ ...x, highlights: {} }));
  const setTypeBoxes = (it, page, arr) => patchItem(it.id, x => ({ ...x, typeBoxes: { ...(x.typeBoxes || {}), [page]: arr } }));
  const setTextBoxes = (it, page, arr) => patchItem(it.id, x => ({ ...x, textBoxes: { ...(x.textBoxes || {}), [page]: arr } }));
  const pageKeyInView = (it) => {
    const sec = document.querySelector('[data-testid="studio-section-' + it.id + '"]');
    const c = canvasRef.current;
    if (!sec || !c) return '1';
    const wraps = sec.querySelectorAll('.studio-page-wrap, .ds-page');
    if (wraps.length <= 1) return '1';
    const mid = c.getBoundingClientRect().top + c.clientHeight / 2;
    let best = 0;
    wraps.forEach((el, i) => { if (el.getBoundingClientRect().top <= mid) best = i; });
    return String(best + 1);
  };
  const addTextBox = (it) => {
    const pg = pageKeyInView(it);
    const curBoxes = ((it.textBoxes || {})[pg]) || [];
    setTextBoxes(it, pg, [...curBoxes, { id: 'tx_' + Math.random().toString(36).slice(2, 8), text: 'Text', x: 8, y: 8, size: 14 }]);
  };
  const addTypeBox = (it) => {
    const pg = pageKeyInView(it);
    const curBoxes = ((it.typeBoxes || {})[pg]) || [];
    const p1Box = (((it.typeBoxes || {})['1']) || [])[0];
    const seed = (pg !== '1' && p1Box && p1Box.text) || (it.type === 'archive' ? it.typeOverlay : ((it.data || {}).corner)) || 'TYPE';
    setTypeBoxes(it, pg, [...curBoxes, { id: 'tb_' + Math.random().toString(36).slice(2, 8), text: seed, x: 62, y: 10 }]);
  };
  const clearAllHighlights = () => {
    mutateItems(items.map(it => ({ ...it, highlights: {} })), 'All highlights cleared across the proposal');
    setConfirmClear(false);
  };
  const duplicateItem = (i) => {
    const it = items[i];
    const copy = JSON.parse(JSON.stringify(it));
    copy.id = 'it_' + Math.random().toString(36).slice(2, 9);
    copy.updatedAt = new Date().toISOString();
    const next = [...items];
    next.splice(i + 1, 0, copy);
    mutateItems(next, 'Datasheet duplicated');
  };
  const setType = (it, v) => {
    if (it.type === 'archive') patchItem(it.id, x => ({ ...x, typeOverlay: v }));
    else patchItem(it.id, x => ({ ...x, data: { ...(x.data || {}), corner: v } }));
  };
  const moveItem = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    mutateItems(next);
    if (selIdx === idx + 1) setSelIdx(j + 1);
    else if (selIdx === j + 1) setSelIdx(idx + 1);
  };

  // Grab-and-drop reordering in the sidebar.
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  function dropReorder(to) {
    const from = dragIdx;
    setDragIdx(null); setOverIdx(null);
    if (from == null || to == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    mutateItems(next);
    setSelIdx(to + 1);
  }
  const dragProps = (i) => ({
    draggable: true,
    onDragStart: (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== i) setOverIdx(i); },
    onDrop: (e) => { e.preventDefault(); dropReorder(i); },
    onDragEnd: () => { setDragIdx(null); setOverIdx(null); },
  });

  // Add datasheets from the library / upload an existing pack (each PDF page
  // becomes its own reorderable entry).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  async function addLibraryItems(products) {
    const next = [...items];
    products.forEach(p => next.push({ id: 'it_' + Math.random().toString(36).slice(2, 9), name: p.name, code: p.code, data: p.data || {}, updatedAt: new Date().toISOString() }));
    await mutateItems(next, products.length + ' datasheet(s) added');
    setPickerOpen(false);
  }
  async function uploadPack(files) {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const next = [...items];
      let pagesAdded = 0;
      for (const f of Array.from(files)) {
        const meta = await api.uploadLegacy(f, (f.name || '').replace(/\.[^.]+$/, ''), 'proposal-pack');
        const isPdf = ((meta.content_type || f.type || '').toLowerCase()).includes('pdf') || /\.pdf$/i.test(f.name || '');
        if (isPdf) {
          const buf = await f.arrayBuffer();
          const doc = await pdfjsLib.getDocument({ data: buf }).promise;
          for (let n = 1; n <= doc.numPages; n++) {
            next.push({
              id: 'it_' + Math.random().toString(36).slice(2, 9), type: 'archive', legacyId: meta.id,
              name: meta.name + (doc.numPages > 1 ? ' — p.' + n : ''), contentType: meta.content_type || 'application/pdf',
              page: n, updatedAt: new Date().toISOString(),
            });
            pagesAdded++;
          }
        } else {
          next.push({ id: 'it_' + Math.random().toString(36).slice(2, 9), type: 'archive', legacyId: meta.id, name: meta.name, contentType: meta.content_type || '', updatedAt: new Date().toISOString() });
          pagesAdded++;
        }
      }
      await mutateItems(next, pagesAdded + ' page(s) added — drag entries in the sidebar to reorder');
    } catch (e) { toast('Upload failed: ' + ((e.response && e.response.data && e.response.data.detail) || e.message), true); }
    setUploading(false);
  }

  // Text-position based highlighter for uploaded PDF datasheets: finds the
  // matched spec tokens in the PDF's text layer and highlights them.
  async function computePdfHighlights(it, specs) {
    const s = specs || {};
    const tokens = ['model', 'wattage', 'cri', 'cct', 'dimming', 'colour']
      .map(k => (s[k] || '').trim()).filter(t => t.length >= 3);
    if (!tokens.length) return {};
    const ct = (it.contentType || '').toLowerCase();
    if (!ct.includes('pdf') && !/\.pdf$/i.test(it.name || '')) return {};
    const { buf } = await getLegacyBuf(it.legacyId);
    const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
    const ntokens = tokens.map(t => t.toUpperCase().replace(/[^A-Z0-9]/g, '')).filter(Boolean);
    const highlights = {};
    for (let n = 1; n <= doc.numPages; n++) {
      if (it.page && n !== it.page) continue;
      const page = await doc.getPage(n);
      const vp = page.getViewport({ scale: 1 });
      const tc = await page.getTextContent();
      const rects = [];
      const tokCount = {};
      for (const item of tc.items) {
        if (rects.length >= 8) break;
        const str = (item.str || '').trim();
        if (str.length < 2 || str.length > 60 || !item.width) continue;
        const nstr = str.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (!nstr) continue;
        let hitTok = null;
        for (const nt of ntokens) {
          if (nstr.includes(nt) || (nstr.length >= 8 && nt.includes(nstr))) { hitTok = nt; break; }
        }
        if (!hitTok) continue;
        tokCount[hitTok] = (tokCount[hitTok] || 0) + 1;
        if (tokCount[hitTok] > 2) continue;
        const h = item.height || Math.abs(item.transform[3]) || 10;
        rects.push({
          x: +((item.transform[4] / vp.width) * 100 - 0.2).toFixed(2),
          y: +(((vp.height - item.transform[5] - h) / vp.height) * 100 - 0.15).toFixed(2),
          w: +((item.width / vp.width) * 100 + 0.4).toFixed(2),
          h: +((h / vp.height) * 100 + 0.3).toFixed(2),
        });
      }
      if (rects.length) highlights[it.page ? '1' : String(n)] = rects;
    }
    return highlights;
  }

  async function applyAiResult(result) {
    const matches = (result && result.matches) || [];
    if (!matches.length) { toast('No matches to apply', true); return; }
    const byItem = {};
    matches.forEach(m => { (byItem[m.item_id] = byItem[m.item_id] || []).push(m); });
    const dedupe = (rects) => {
      const seen = {};
      return rects.filter(r => { const k = [r.x, r.y, r.w, r.h].join(','); if (seen[k]) return false; seen[k] = 1; return true; });
    };
    let copies = 0;
    const next = [];
    for (const it of items) {
      const ms = byItem[it.id];
      if (!ms || !ms.length) { next.push(it); continue; }
      for (let idx = 0; idx < ms.length; idx++) {
        const m = ms[idx];
        const ref = (m.refs || []).filter(Boolean).join(' / ');
        if (it.type === 'archive') {
          let hl = {};
          try { hl = await computePdfHighlights(it, m.matched_specs); }
          catch (e) { console.warn('pdf highlight failed for', it.name, e); }
          const hasHl = Object.keys(hl).length > 0;
          if (idx === 0) next.push({ ...it, typeOverlay: ref || it.typeOverlay || '', highlights: hasHl ? hl : it.highlights, updatedAt: new Date().toISOString() });
          else { copies++; next.push({ ...it, id: 'it_' + Math.random().toString(36).slice(2, 9), typeOverlay: ref, highlights: hasHl ? hl : {}, updatedAt: new Date().toISOString() }); }
          continue;
        }
        // Measure against the original item's hidden render (copies share the same data layout).
        const auto = computeAutoHighlights(it.id, m.matched_specs);
        if (idx === 0) {
          const existing = ((it.highlights || {})['1']) || [];
          next.push({
            ...it,
            data: { ...(it.data || {}), corner: ref || (it.data || {}).corner || '' },
            highlights: { ...(it.highlights || {}), '1': dedupe([...existing, ...auto]) },
            updatedAt: new Date().toISOString(),
          });
        } else {
          copies++;
          next.push({
            ...it,
            id: 'it_' + Math.random().toString(36).slice(2, 9),
            data: { ...(it.data || {}), corner: ref },
            highlights: { '1': dedupe(auto) },
            updatedAt: new Date().toISOString(),
          });
        }
      }
    }
    await mutateItems(next, matches.length + ' quote match(es) applied' + (copies ? ' · ' + copies + ' variation cop' + (copies > 1 ? 'ies' : 'y') + ' created' : '') + ' — refs + highlights added');
  }

  useEffect(() => {
    function esc(e) { if (e.key === 'Escape') setTool('none'); }
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const curItem = cur.it;
  const typeVal = curItem ? (curItem.type === 'archive' ? (curItem.typeOverlay || '') : ((curItem.data || {}).corner || '')) : '';

  // Continuous scroll: all pages render stacked; the sidebar tracks whichever
  // section is at the top of the viewport (scroll spy), clicking jumps to it.
  const canvasRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(entries => setCanvasWidth(entries[0].contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const compactCanvas = canvasWidth > 0 && canvasWidth < 900;
  const maxZoom = canvasWidth ? Math.min(1.3, (canvasWidth - 2) / (compactCanvas ? 794 : 1138)) : 1.3;
  const effectiveZoom = Math.min(zoom, maxZoom);
  const sectionRefs = useRef({});
  const spyLock = useRef(0);
  function handleScroll() {
    if (Date.now() < spyLock.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const topEdge = c.getBoundingClientRect().top + 120;
    let best = 0;
    for (let i = 0; i < entries.length; i++) {
      const el = sectionRefs.current[i];
      if (!el) continue;
      if (el.getBoundingClientRect().top <= topEdge) best = i; else break;
    }
    if (best !== selIdx) setSelIdx(best);
  }
  function goTo(i) {
    setSelIdx(i);
    spyLock.current = Date.now() + 900;
    const el = sectionRefs.current[i];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className={'studio-overlay' + (compactCanvas ? ' studio-compact' : '')} data-testid="proposal-studio">
      <div className="studio-top">
        <strong>Proposal Studio</strong>
        <span className="studio-proj-name">{form.name}</span>
        <div className="studio-top-actions">
          <button className={'btn btn-sm studio-tool' + (tool === 'hl' ? ' on' : '')} data-testid="studio-highlighter"
            onClick={() => setTool(t => (t === 'hl' ? 'none' : 'hl'))} title="Drag on the page to highlight. Esc to stop.">
            Highlighter {tool === 'hl' ? 'ON' : 'OFF'}
          </button>
          <button className="btn btn-sm" data-testid="studio-ai-match" onClick={() => setAiOpen(true)}>AI Match Quote</button>
          <button className="btn btn-sm" data-testid="studio-clear-all" onClick={() => setConfirmClear(true)} disabled={items.length === 0}>Clear ALL highlights</button>
          <div className="studio-zoom-ctl">
            <button className="btn btn-sm" data-testid="studio-zoom-out" title="Zoom out" disabled={effectiveZoom <= .25} onClick={() => setZoom(Math.max(.25, effectiveZoom - .12))}>−</button>
            <span data-testid="studio-zoom-value">{Math.round(effectiveZoom * 100)}%</span>
            <button className="btn btn-sm" data-testid="studio-zoom-in" title="Zoom in to fit" disabled={effectiveZoom >= maxZoom} onClick={() => setZoom(Math.min(maxZoom, effectiveZoom + .12))}>＋</button>
          </div>
          <button className="btn btn-sm btn-primary" data-testid="studio-export" onClick={exportPdf} disabled={busy || items.length === 0} title="Combined PDF: cover + every datasheet with highlights and TYPE refs">{busy ? 'Exporting…' : 'Export Proposal PDF'}</button>
          <button className="btn btn-sm" data-testid="studio-close" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="studio-body">
        <aside className="studio-side">
          <div className="studio-side-head">Pages · click to jump, drag ⠿ to reorder</div>
          <div className="studio-side-tools">
            <button className="btn btn-sm" data-testid="studio-add-datasheets" onClick={() => setPickerOpen(true)}>＋ Datasheets</button>
            <FileUpload className="btn btn-sm" testid="studio-upload-pack" label={uploading ? 'Uploading…' : '＋ Upload pack'} disabled={uploading} multiple accept="application/pdf,image/*" onFiles={uploadPack} />
          </div>
          <div className={'studio-entry' + (selIdx === 0 ? ' active' : '')} data-testid="studio-entry-cover" onClick={() => goTo(0)}>
            <span className="nm">Cover page</span>
          </div>
          {items.map((it, i) => (
            <div key={it.id}
              className={'studio-entry' + (selIdx === i + 1 ? ' active' : '') + (dragIdx === i ? ' dragging' : '') + (overIdx === i && dragIdx != null && dragIdx !== i ? ' drag-over' : '')}
              data-testid={'studio-entry-' + it.id} onClick={() => goTo(i + 1)} {...dragProps(i)}>
              <span className="drag-handle" title="Drag to reorder">⠿</span>
              <span className="nm">{(it.data && it.data.name) || it.name || '(Untitled)'}
                {it.type === 'archive' ? <em> · PDF</em> : null}
                {(it.type === 'archive' ? it.typeOverlay : (it.data || {}).corner) ? <b className="ref"> {it.type === 'archive' ? it.typeOverlay : (it.data || {}).corner}</b> : null}
              </span>
              <span className="ord">
                <button className="btn btn-sm" data-testid={'studio-dup-' + it.id} title="Duplicate this datasheet" onClick={e => { e.stopPropagation(); duplicateItem(i); }}>⧉</button>
                <button className="btn btn-sm" data-testid={'studio-up-' + it.id} disabled={i === 0} onClick={e => { e.stopPropagation(); moveItem(i, -1); }}>↑</button>
                <button className="btn btn-sm" data-testid={'studio-down-' + it.id} disabled={i === items.length - 1} onClick={e => { e.stopPropagation(); moveItem(i, 1); }}>↓</button>
              </span>
            </div>
          ))}
        </aside>

        <div className="studio-main">
          {curItem && (
            <div className="studio-item-bar">
              <span className="studio-cur-name">{(curItem.data && curItem.data.name) || curItem.name || ''}</span>
              <label>TYPE box</label>
              <input className="input studio-type-input" data-testid="studio-type-input" key={curItem.id} defaultValue={typeVal}
                placeholder="e.g. A1 / BD1T" onBlur={e => { if (e.target.value !== typeVal) setType(curItem, e.target.value); }} />
              {cur.kind === 'sheet' && <button className="btn btn-sm" data-testid="studio-edit-datasheet" onClick={() => onEditItem(curItem)}>Edit datasheet</button>}
              <button className="btn btn-sm" data-testid="studio-add-typebox" title="Add a movable TYPE box — drag it anywhere on the page" onClick={() => addTypeBox(curItem)}>＋ TYPE box</button>
              <button className="btn btn-sm" data-testid="studio-add-textbox" title="Add a movable Arial text box — drag it anywhere, A−/A＋ to resize" onClick={() => addTextBox(curItem)}>＋ Text box</button>
              <button className="btn btn-sm" data-testid="studio-clear-highlights" onClick={() => clearRects(curItem)}>Clear highlights</button>
              {tool === 'hl' && <span className="studio-hint">Drag on the page to highlight · click ✕ on a mark to remove · Esc to stop</span>}
            </div>
          )}
          <div className="studio-canvas" data-testid="studio-canvas" ref={canvasRef} onScroll={handleScroll}>
            <div data-testid="studio-scaled-pages" style={{ zoom: effectiveZoom, width: compactCanvas ? 794 : undefined }}>
              {entries.map((en, i) => (
                <section key={en.it ? en.it.id : 'cover'} ref={el => { sectionRefs.current[i] = el; }} className="studio-section" data-testid={'studio-section-' + (en.it ? en.it.id : 'cover')}>
                  {en.kind === 'cover' && (
                    <div className="studio-cover-split">
                      <div className="studio-page-wrap"><CoverPage name={form.name} cover={cover} /></div>
                        <div className="studio-cover-form" style={compactCanvas ? { zoom: 1 / effectiveZoom, width: 794 * effectiveZoom } : undefined}>
                        <h4>Cover details</h4>
                        <L label="Job / project name"><input className="input" data-testid="studio-cover-name" value={form.name} onChange={e => setField('name', e.target.value)} /></L>
                        <L label="Sales rep (prepared by)"><input className="input" data-testid="studio-cover-rep" value={cover.preparedBy || ''} onChange={e => setCover('preparedBy', e.target.value)} /></L>
                        <L label="Email"><input className="input" value={cover.preparedByEmail || ''} onChange={e => setCover('preparedByEmail', e.target.value)} /></L>
                        <L label="Date presented"><input className="input" type="date" value={cover.datePresented || ''} onChange={e => setCover('datePresented', e.target.value)} /></L>
                        <L label="Revision"><input className="input" value={cover.revision || ''} onChange={e => setCover('revision', e.target.value)} /></L>
                        <L label="Address"><input className="input" value={cover.address || ''} onChange={e => setCover('address', e.target.value)} /></L>
                        <L label="Telephone"><input className="input" value={cover.telephone || ''} onChange={e => setCover('telephone', e.target.value)} /></L>
                      </div>
                    </div>
                  )}
                  {en.kind === 'sheet' && (
                    <div className="studio-sheet-pages">
                      {isUsPackSheet(en.it.data, region) ? (
                        <USDatasheetPreview sheet={en.it.data} />
                      ) : (
                      <DatasheetPreview sheet={en.it.data}
                        overlay={<>
                          <DrawLayer rects={(en.it.highlights || {})['1']} active={tool === 'hl'}
                            onAdd={rs => addRects(en.it, '1', rs)} onRemove={ri => removeRect(en.it, '1', ri)} />
                          <TypeBoxLayer boxes={(en.it.typeBoxes || {})['1']} onChange={bs => setTypeBoxes(en.it, '1', bs)} />
                          <TextBoxLayer boxes={(en.it.textBoxes || {})['1']} onChange={bs => setTextBoxes(en.it, '1', bs)} />
                        </>}
                        overlay2={sheetHasPage2(en.it.data)
                          ? <>
                            <DrawLayer rects={(en.it.highlights || {})['2']} active={tool === 'hl'}
                              onAdd={rs => addRects(en.it, '2', rs)} onRemove={ri => removeRect(en.it, '2', ri)} />
                            <TypeBoxLayer boxes={(en.it.typeBoxes || {})['2']} onChange={bs => setTypeBoxes(en.it, '2', bs)} />
                            <TextBoxLayer boxes={(en.it.textBoxes || {})['2']} onChange={bs => setTextBoxes(en.it, '2', bs)} />
                          </>
                          : null} />
                      )}
                    </div>
                  )}
                  {en.kind === 'archive' && (
                    <ArchivePages item={en.it} tool={tool}
                      onAddRect={(pg, rs) => addRects(en.it, pg, rs)} onRemoveRect={(pg, ri) => removeRect(en.it, pg, ri)}
                      onTypeBoxes={(pg, bs) => setTypeBoxes(en.it, pg, bs)} onTextBoxes={(pg, bs) => setTextBoxes(en.it, pg, bs)} />
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>

      {aiOpen && (
        <AiMatchModal items={items} form={form} cover={cover} setField={setField} setCover={setCover}
          onApply={applyAiResult} onClose={() => setAiOpen(false)} toast={toast} />
      )}

      {pickerOpen && <LibraryPicker onClose={() => setPickerOpen(false)} onAdd={addLibraryItems} />}

      {confirmClear && (
        <div className="modal-overlay" data-testid="clear-all-modal" onMouseDown={() => setConfirmClear(false)}>
          <div className="modal" style={{ maxWidth: 430 }} onMouseDown={e => e.stopPropagation()}>
            <h3>Clear ALL highlights?</h3>
            <p className="modal-hint">This removes every highlighter mark on every page of this proposal. TYPE boxes and refs are kept. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn" onClick={() => setConfirmClear(false)}>Cancel</button>
              <button className="btn btn-primary" data-testid="studio-clear-all-confirm" onClick={clearAllHighlights}>Yes — clear all highlights</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function L({ label, children }) { return <div className="field"><label className="field-label">{label}</label>{children}</div>; }
